/**
 * RWBR — a race crest, wherever a race is named.
 *
 * D-026: race marks belong "in the race header, the post-race summary and the
 * season schedule." This is the one component all three use, so a crest cannot
 * come out a different size or a different shape on one screen.
 *
 * ===========================================================================
 * TWO THINGS THIS DOES THAT ARE NOT DECORATION
 * ===========================================================================
 *
 * 1 · IT RESERVES ITS SPACE BEFORE THE IMAGE LOADS. D-026's own note, from the
 *     golf sibling that solved this first: *"reserve layout space before the
 *     image loads."* The box is sized from the `size` prop and never from the
 *     file, so a crest arriving late slides nothing. A race header that jumps
 *     when a logo lands is worse than a header with no logo.
 *
 * 2 · IT RENDERS AN `<img>` AND NEVER AN INLINE `<svg>`. Marks may be uploaded
 *     as SVG — a crest usually is vector — and an SVG is a document that can
 *     carry `<script>`. Inlined into this DOM it would run here; through an
 *     `<img>` it cannot, in any browser. The rule is repeated in
 *     `worker/logo.ts` and in `CarTopDown.tsx` because the day somebody
 *     "improves" it into an inline `<svg>` so it can be recoloured by CSS, the
 *     reason has to already be on the page they are editing.
 *
 * RENDERS NOTHING AT ALL when the race has no mark, rather than a placeholder.
 * Almost no race has one until Howard uploads it, and 45 grey boxes down a
 * schedule would be a worse screen than 45 race names.
 */

import { useState } from 'react';
import { logoForRace, logoForTrack, useLogos, type StoredLogo } from '../lib/logoStore.ts';

export interface RaceLogoProps {
  /** `${trackId}#${variant}` — `ScheduledRace.raceKey`. */
  raceKey: string | null | undefined;
  /** Box edge in px. The mark is fitted inside it, never stretched. */
  size?: number;
  className?: string;
}

export default function RaceLogo({ raceKey, size = 40, className }: RaceLogoProps) {
  return <MarkImage logo={logoForRace(useLogos(), raceKey)} size={size} className={className} />;
}

/** D-026's third mark — a track's own crest, by track card id. Nothing when there is none. */
export function TrackMark({ trackId, size = 22, className }: { trackId: string | null | undefined; size?: number; className?: string }) {
  return <MarkImage logo={logoForTrack(useLogos(), trackId)} size={size} className={className} />;
}

function MarkImage({ logo, size, className }: { logo: StoredLogo | null; size: number; className?: string }) {
  /**
   * A mark whose FILE has gone (deleted from the bucket, row still there)
   * renders a broken-image glyph in the middle of a race header. Once the load
   * has failed there is nothing to show and the component stands down — the
   * header reads exactly as it did before anyone uploaded anything.
   */
  const [broken, setBroken] = useState(false);

  if (!logo || broken) return null;

  return (
    <span
      className={`racelogo${className ? ` ${className}` : ''}`}
      style={{ width: size, height: size }}
      // The race is named in text beside this on every screen that uses it, so
      // the mark is decoration and is hidden from screen readers rather than
      // announcing the race a second time.
      aria-hidden
    >
      <img
        src={logo.url}
        alt=""
        width={size}
        height={size}
        loading="lazy"
        decoding="async"
        onError={() => setBroken(true)}
        title={logo.label}
      />
    </span>
  );
}
